/**
 * process-columns.tsx — wspólne kolumny PRZEKROJOWE PROCESU („Aktywny proces", „Bieżący etap")
 * list dokumentów.
 *
 * Jedno źródło prawdy dla wszystkich klas dokumentów (faktury zakupu i sprzedaży, umowy, pisma,
 * handlowe, wewnętrzne, specjalne) i dla dwóch ekranów, które pokazują je jako tabelę z kolumnami „jak na
 * liście": strony list oraz sekcje kokpitu decyzji (`pages/processes/decision-sections`). Kolumny są
 * IDENTYCZNE we wszystkich klasach poza prefiksem kluczy i18n nagłówków (`labelKeyPrefix`, np.
 * `tenant.saleInvoices`, `tenant.documents.contracts`).
 *
 * Kolumny są poza katalogiem pól (pola WIRTUALNE silnika list — `apps/api/lib/list-engine/providers/
 * process-columns.ts`), więc nie wchodzą do mapy z `buildCoreColumns`; ich id → slug backendu
 * (`?columns=`) daje `PROCESS_COLUMN_ID_TO_SLUG`.
 */

import type { ColumnDefinition } from '@/components/configurable-table';

/**
 * id kolumn przekrojowych procesu → slug backendu dla `?columns=` (id = slug). Kolumny są poza
 * katalogiem pól (pola WIRTUALNE silnika list), więc nie wchodzą do mapy z `buildCoreColumns`.
 */
export const PROCESS_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  active_process_name: 'active_process_name',
  current_stage: 'current_stage',
};

/**
 * Pola wiersza listy dokumentów, które czytają kolumny procesu. Silnik list dokłada je na każdym
 * wierszu, gdy tenant ma aktywny moduł procesów, a pomija całkiem (klucz nieobecny), gdy moduł jest
 * nieaktywny — stąd oba opcjonalne. `currentStage` to bieżące etapy sprawy w toku (0..N, gałęzie
 * równoległe) albo nazwa etapu końcowego sprawy zakończonej (jeden element).
 *
 * Uwaga: oba pola są opcjonalne, więc to typ „słaby" (weak type) — kompilator odrzuca wiersz bez
 * ŻADNEGO z tych pól, ale NIE wykryje wiersza, który ma tylko jedno z nich (druga kolumna pokaże wtedy
 * same myślniki). Pełnego pokrycia nie da się wymusić typem bez uczynienia pól wymaganymi.
 */
export interface ProcessColumnsRow {
  activeProcessName?: string | null;
  currentStage?: string[];
}

/**
 * Kolumny przekrojowe silnika list (poza katalogiem pól) — „Aktywny proces" i „Bieżący etap".
 * Domyślnie widoczne gdy moduł procesu aktywny (inaczej byłyby kolumnami samych myślników).
 * Budują je strony list dokumentów i sekcje kokpitu decyzji (tam moduł procesów jest z definicji
 * aktywny).
 *
 * `labelKeyPrefix` — prefiks kluczy i18n kartoteki; nagłówki to `<prefiks>.columns.activeProcessName`
 * i `<prefiks>.columns.currentStage`.
 *
 * `isSortable` — serwerowa sortowalność kolumny systemowej (`meta.sortable` ostatniej odpowiedzi);
 * „Bieżący etap" jest niesortowalny zawsze (etap bywa wielowartościowy — gałęzie równoległe).
 *
 * `Row` musi spełniać `ProcessColumnsRow` (pola `activeProcessName`/`currentStage`, które deklaruje
 * każdy interfejs wiersza listy dokumentów) — kolumny czytają je przez `accessor` po nazwie, więc
 * wiersz bez nich dawałby kolumny samych myślników. Ograniczenie typu łapie wiersz bez ŻADNEGO z pól
 * procesu (`ProcessColumnsRow` ma tylko pola opcjonalne, więc działa jak typ „słaby"); braku jednego
 * z nich nie wykrywa — pilnuje tego dyscyplina deklaracji wiersza (patrz opis `ProcessColumnsRow`).
 */
export function buildProcessColumns<Row extends ProcessColumnsRow>(args: {
  t: (key: string) => string;
  labelKeyPrefix: string;
  isSortable: (slug: string) => boolean;
  isProcessModuleActive: boolean;
}): ColumnDefinition<Row>[] {
  const { t, labelKeyPrefix, isSortable, isProcessModuleActive } = args;
  return [
    {
      id: 'active_process_name',
      accessor: 'activeProcessName',
      header: t(`${labelKeyPrefix}.columns.activeProcessName`),
      defaultVisible: isProcessModuleActive,
      defaultWidth: 180,
      sortable: isSortable('active_process_name'),
      headerFilter: { category: 'text', fieldSlug: 'active_process_name', conditionOnly: true },
      renderCell: (value) => (
        <span className="text-text-secondary">{value ? String(value) : '—'}</span>
      ),
    },
    {
      id: 'current_stage',
      accessor: 'currentStage',
      header: t(`${labelKeyPrefix}.columns.currentStage`),
      defaultVisible: isProcessModuleActive,
      defaultWidth: 200,
      // Etap bywa wielowartościowy (gałęzie równoległe) — silnik nie sortuje po tym polu.
      sortable: false,
      // `operators: ['eq']` (okno filtra w nagłówku) — backend (`buildStageFilter` w
      // process-columns.ts) zna dwa dopasowania etykiety etapu: dokładne i „zawiera”. „Zawiera”
      // wysyła wiersz filtrów (jego domyślny operator tekstowy); pozostałe operatory tekstowe
      // backend pominąłby (filtr bez warunku), więc okno ich nie oferuje.
      headerFilter: {
        category: 'text',
        fieldSlug: 'current_stage',
        conditionOnly: true,
        operators: ['eq'],
      },
      renderCell: (value) => {
        const stages = Array.isArray(value) ? (value as string[]) : [];
        return (
          <span className="text-text-secondary">{stages.length > 0 ? stages.join(', ') : '—'}</span>
        );
      },
    },
  ];
}
